import React from 'react';
import { Typography } from 'antd';

const { Text } = Typography;

// Convert a snake_case / camelCase column key into a readable label.
const COLUMN_LABELS = {
  ride_id: 'Ride ID',
  seq_id: 'Ride ID',
  rider_name: 'Rider Name',
  rider: 'Rider Name',
  health_org: 'Health Organization',
  health_organization: 'Health Organization',
  health_organization_name: 'Health Organization',
  ho_name: 'Health Organization',
  organization: 'Health Organization',
  org: 'Health Organization',
  org_name: 'Health Organization',
  tc_company: 'Transportation Provider',
  tc: 'Transportation Provider',
  tc_name: 'Transportation Provider',
  transport_company: 'Transportation Provider',
  transport_co: 'Transportation Provider',
  transport_company_name: 'Transportation Provider',
  transportation_company: 'Transportation Provider',
  transportation_provider: 'Transportation Provider',
  status: 'Ride Status',
  ride_status: 'Ride Status',
  pickup_date_time: 'Pickup Date & Time',
  pickup_datetime: 'Pickup Date & Time',
  pick_up_date_time: 'Pickup Date & Time',
  pickup_date: 'Pickup Date',
  pick_up_date: 'Pickup Date',
  completed_date_time: 'Completed Date & Time',
  completed_datetime: 'Completed Date & Time',
  completed_at: 'Completed',
  completed: 'Completed',
  unload_time: 'Completed',
  created_at: 'Date Created',
  created_date: 'Date Created',
  date_created: 'Date Created',
  updated_at: 'Updated',
  vehicle_type: 'Vehicle Type',
  vehicle: 'Vehicle Type',
  driver: 'Driver',
  driver_name: 'Driver Name',
  mileage: 'Mileage',
  act_mileage: 'Mileage',
  actual_mileage: 'Actual Ride Mileage',
  estimated_mileage: 'Estimated Ride Mileage',
  estimated_duration: 'Estimated Ride Duration',
  appointment_date: 'Appointment Date',
  appointment_date_time: 'Appointment Date & Time',
  appointment_datetime: 'Appointment Date & Time',
  cost_center: 'Cost Center',
  pickup_address: 'Pickup Location',
  pickup_location: 'Pickup Location',
  dropoff_address: 'Destination',
  drop_off_address: 'Destination',
  destination: 'Destination',
  destination_address: 'Destination',
  pickup_county: 'Pickup County',
  dropoff_county: 'Dropoff County',
  drop_off_county: 'Dropoff County',
  wait_time: 'Wait Time',
  created_by: 'Created By',
  is_recurring: 'Recurring',
  recurring: 'Recurring',
  billed_amount: 'Billed Amount',
  invoice_id: 'Invoice ID',
  date_of_birth: 'Date of Birth',
  dob: 'Date of Birth',
  phone_number: 'Phone Number',
  actual_pickup: 'Actual Pickup',
  actual_pickup_date: 'Actual Pickup',
  actual_pick_up_date: 'Actual Pickup',
  acceptance_time: 'Accepted',
  accepted_at: 'Accepted',
  deadhead_mileage: 'Deadhead Mileage',
  total_rides: 'Total Rides',
  ride_count: 'Total Rides',
  completion_rate: 'Completion Rate',
  cancellation_rate: 'Cancellation Rate',
};

const normalizeColumnKey = (key) =>
  String(key)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase();

export const formatColumnLabel = (key) => {
  const normalized = normalizeColumnKey(key);
  if (COLUMN_LABELS[normalized]) return COLUMN_LABELS[normalized];

  return normalized
    .split('_')
    .filter(Boolean)
    .map((w) => (w === 'id' ? 'ID' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

const VISIBLE_ID_COLUMNS = new Set(['ride_id', 'seq_id', 'invoice_id', 'uber_ride_id']);

export const isInternalIdColumn = (key) => {
  const normalized = normalizeColumnKey(key);
  if (VISIBLE_ID_COLUMNS.has(normalized)) return false;
  return normalized === 'id' || /(?:^|_)(?:id|uuid)$/.test(normalized);
};

// Render inline markdown — **bold** and `code` spans — inside a line of text.
export const renderInline = (text) => {
  const segments = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return segments.map((seg, i) => {
    const bold = seg.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <Text strong key={i}>{bold[1]}</Text>;
    const code = seg.match(/^`([^`]+)`$/);
    if (code) {
      return (
        <Text key={i} code style={{ fontSize: 13 }}>
          {code[1]}
        </Text>
      );
    }
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
};

// Render a block of light markdown: headings, bullets and plain lines.
export const renderSummary = (text) => {
  const lines = String(text).split('\n');
  const out = [];
  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const heading = trimmed.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      out.push(
        <Text strong key={i} style={{ display: 'block', marginTop: out.length ? 8 : 0 }}>
          {renderInline(heading[1])}
        </Text>,
      );
    } else {
      const bullet = trimmed.match(/^[*-]\s+(.*)$/);
      if (bullet) {
        out.push(
          <span key={i} style={{ display: 'block', paddingLeft: 14, textIndent: -8 }}>
            <span style={{ marginRight: 6 }}>•</span>
            {renderInline(bullet[1])}
          </span>,
        );
      } else {
        out.push(
          <span key={i} style={{ display: 'block' }}>
            {renderInline(trimmed)}
          </span>,
        );
      }
    }
  });
  return out;
};

const FOLLOW_UP_OPENERS =
  /^(what|which|who|whose|when|where|why|how|show|list|display|provide|plot|chart|graph|map|segment|count|calculate|compute|describe|detail|group|analy[sz]e|compare|contrast|identify|predict|forecast|estimate|break down|give|find|explore|investigate|examine|review|rank|highlight|summari[sz]e|tell me|walk me through|look at|dive into|can you|could you|drill|dig)\b/i;

const MAX_FOLLOW_UPS = 4;

const looksLikeFollowUp = (line) => {
  if (line.length < 10 || line.length > 180) return false;

  if (/^([#>*\-+|`]|\d+[.)]\s)/.test(line)) return false;

  if (line.includes('**') || line.includes('`')) return false;

  if (line.endsWith(':')) return false;
  return line.endsWith('?') || FOLLOW_UP_OPENERS.test(line);
};


export const splitFollowUps = (text) => {
  const source = String(text || '');
  const lines = source.split('\n');
  const followUps = [];
  let cut = lines.length;

  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      if (followUps.length) break; // a blank line closes the run
      cut = i; // trailing blank — keep scanning
      continue;
    }
    if (followUps.length >= MAX_FOLLOW_UPS || !looksLikeFollowUp(trimmed)) break;
    followUps.unshift(trimmed);
    cut = i;
  }

  const body = lines.slice(0, cut).join('\n').trim();
  if (!body) return { body: source.trim(), followUps: [] };

  return { body, followUps: [...new Set(followUps)] };
};
